import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { api } from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import AppleSelect from "../Common/AppleSelect";
import { Modal, ModalFooter } from "../../stock/StockUI";
import { inputClass } from "../../ClientsTable/clientFormUI";
import DateInput from "../Common/DateInput";

const labelClass = "text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block";

const BatchProductionModal = ({ recipe = null, onClose, isDarkMode }) => {
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();

    // Idempotency key (règle absolue #10) : généré une seule fois à l'ouverture
    // du formulaire et réutilisé pour TOUTE tentative de soumission de ce même
    // lot (double-clic, retry réseau) — le backend (BatchProductionStateProcessor)
    // ignore une 2e requête avec le même id au lieu de redéduire le stock.
    const [batchId] = useState(() => (
        typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`
    ));

    const { register, control, handleSubmit, formState: { errors } } = useForm({
        defaultValues: {
            recipe_id: recipe?.id || '',
            quantity: 1,
            production_date: new Date().toISOString().split('T')[0],
            note: '',
            allow_negative_stock: false
        }
    });

    // Fetch recipes for dropdown
    const { data: recipesData } = useQuery({
        queryKey: ['recipes-for-batch'],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/recipes?is_sub_recipe=true', {
                headers: { 'X-account-id': accountId.id },
                params: { itemsPerPage: 200 }
            });
            return response.data;
        },
        enabled: !recipe // Only fetch if recipe not provided
    });

    const mutation = useMutation({
        mutationFn: async (data) => {
            const accountId = await secureStorage.getItem("selectedAccount");
            return api.post('/batch-productions', data, {
                headers: { 'X-account-id': accountId.id }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['batch-productions']);
            onClose();
        }
    });

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            await mutation.mutateAsync({ ...data, id: batchId });
        } catch (error) {
            alert('Erreur lors de la création du lot');
        } finally {
            setIsLoading(false);
        }
    };

    const fieldClass = inputClass(false);

    return (
        <Modal
            title="Nouveau lot de production"
            subtitle={recipe?.name}
            onClose={onClose}
            size="lg"
            footer={
                <ModalFooter onCancel={onClose} onConfirm={handleSubmit(onSubmit)} saving={isLoading}
                             label={isLoading ? 'Création...' : 'Créer le lot'}/>
            }
        >
            <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* Recipe Selection */}
                <div className="sm:col-span-2">
                    <label className={labelClass}>Recette <span className="text-red-500">*</span> :</label>
                    <AppleSelect
                        {...register("recipe_id", { required: true })}
                        disabled={!!recipe}
                        className={`${fieldClass} ${recipe ? 'opacity-60' : ''}`}
                    >
                        {recipe ? (
                            <option value={recipe.id}>{recipe.name}</option>
                        ) : (
                            <>
                                <option value="">Sélectionner une recette</option>
                                {recipesData?.data?.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </>
                        )}
                    </AppleSelect>
                    {errors.recipe_id && <p className="text-red-500 text-xs mt-1">Recette requise</p>}
                </div>

                {/* Quantity */}
                <div>
                    <label htmlFor="batch-quantity" className={labelClass}>Quantité <span className="text-red-500">*</span> :</label>
                    <input id="batch-quantity" type="number" min="1"
                           {...register("quantity", { required: true, min: 1 })}
                           className={fieldClass}/>
                    {errors.quantity && <p className="text-red-500 text-xs mt-1">Quantité requise (min: 1)</p>}
                </div>

                {/* Production Date */}
                <div>
                    <label htmlFor="batch-date" className={labelClass}>Date de production <span className="text-red-500">*</span> :</label>
                    <Controller name="production_date" control={control} rules={{ required: true }}
                                render={({ field }) => (
                                    <DateInput id="batch-date" required value={field.value} name={field.name}
                                               onChange={(e) => field.onChange(e.target.value)} className={fieldClass}/>
                                )}/>
                    {errors.production_date && <p className="text-red-500 text-xs mt-1">Date requise</p>}
                </div>

                {/* Note */}
                <div className="sm:col-span-2">
                    <label htmlFor="batch-note" className={labelClass}>Note (optionnelle) :</label>
                    <textarea id="batch-note" {...register("note")} rows={3} className={fieldClass}/>
                </div>

                {/* Allow Negative Stock */}
                <label htmlFor="allow_negative_stock"
                       className="sm:col-span-2 flex items-center gap-3 py-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="checkbox" id="allow_negative_stock" {...register("allow_negative_stock")}
                           className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"/>
                    Autoriser le stock négatif
                </label>
            </form>
        </Modal>
    );
};

export default BatchProductionModal;
